import { FC, memo, useCallback, useMemo } from 'react';
import { Handle, type NodeProps, Position } from 'reactflow';

import { ErdNodeData } from '../types';

export const ErdEntityNode: FC<NodeProps<ErdNodeData>> = memo(({ data }) => {
  const { model, connectedColumns, isSelected, isFocused, isDimmed, onSelect } = data;

  const handleClick = useCallback(() => {
    onSelect(model.uniqueId);
  }, [onSelect, model.uniqueId]);

  const borderClass = isFocused
    ? 'border-borderBrand ring-2 ring-borderBrand ring-opacity-30'
    : isSelected
      ? 'border-borderBrand'
      : 'border-borderMuted';

  // Show columns that participate in relationships.
  // If catalog data is available, use it; otherwise create synthetic entries
  // from the relationship metadata so columns always appear on the node.
  const visibleColumns = useMemo(() => {
    const catalogMatch = model.columns.filter((col) =>
      connectedColumns.has(col.name.toLowerCase()),
    );

    // Build a set of column names already covered by catalog
    const coveredNames = new Set(catalogMatch.map((c) => c.name.toLowerCase()));

    // Add synthetic entries for connected columns missing from catalog
    const synthetic = [...connectedColumns]
      .filter((colName) => !coveredNames.has(colName))
      .map((colName) => ({
        name: colName,
        type: '',
        description: '',
        isPrimaryKey: false,
        isForeignKey: false,
      }));

    return [...catalogMatch, ...synthetic];
  }, [model.columns, connectedColumns]);

  return (
    <div
      className={`cursor-pointer rounded-lg border bg-bgMain shadow-sm transition-all ${borderClass} ${isDimmed ? 'opacity-50' : ''}`}
      style={{ width: 260 }}
      onClick={handleClick}
      data-testid={`erd-node-${model.name}`}
    >
      {/* Table header */}
      <div className="rounded-t-lg border-b border-borderMuted bg-bgNeutralMuted px-3 py-2">
        <div className="flex items-center gap-2">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            className="shrink-0 text-fgBrand"
          >
            <rect
              x="1"
              y="1"
              width="10"
              height="10"
              rx="2"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <line
              x1="1"
              y1="4.5"
              x2="11"
              y2="4.5"
              stroke="currentColor"
              strokeWidth="1"
            />
            <line
              x1="5.5"
              y1="4.5"
              x2="5.5"
              y2="11"
              stroke="currentColor"
              strokeWidth="1"
            />
          </svg>
          <span
            className="truncate text-sm font-semibold text-fgMain"
            title={model.name}
          >
            {model.name}
          </span>
        </div>
      </div>

      {/* Connected columns */}
      {visibleColumns.length > 0 ? (
        <div className="py-0.5">
          {visibleColumns.map((col) => (
            <div
              key={col.name}
              className="relative flex items-center gap-1.5 border-b border-borderMuted px-3 py-1 text-xs last:border-b-0"
            >
              {col.isPrimaryKey && (
                <span className="shrink-0 rounded bg-bgWarningMuted px-1 text-[9px] font-bold text-fgWarning">
                  PK
                </span>
              )}
              {col.isForeignKey && (
                <span className="shrink-0 rounded bg-bgBrandMuted px-1 text-[9px] font-bold text-fgBrand">
                  FK
                </span>
              )}
              <span className="truncate text-fgMain">{col.name}</span>
              <span className="ml-auto shrink-0 text-[9px] text-fgDecorative">
                {col.type}
              </span>
              {/* Symmetric handles: both source+target on each side per column */}
              <Handle
                type="target"
                position={Position.Left}
                id={`col-${col.name.toLowerCase()}`}
                className="!h-[6px] !w-[6px] !border-borderBrand !bg-fgBrand"
                style={{ top: 'auto', left: -3 }}
                isConnectable={false}
              />
              <Handle
                type="source"
                position={Position.Right}
                id={`col-${col.name.toLowerCase()}`}
                className="!h-[6px] !w-[6px] !border-borderBrand !bg-fgBrand"
                style={{ top: 'auto', right: -3 }}
                isConnectable={false}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="px-3 py-2 text-[10px] text-fgDecorative">
          No connected columns
        </div>
      )}

      {/* Fallback handles for nodes with no column-level connections */}
      {visibleColumns.length === 0 && (
        <>
          <Handle
            type="target"
            position={Position.Left}
            className="!h-[6px] !w-[6px] !border-borderBrand !bg-fgBrand"
            isConnectable={false}
          />
          <Handle
            type="source"
            position={Position.Right}
            className="!h-[6px] !w-[6px] !border-borderBrand !bg-fgBrand"
            isConnectable={false}
          />
        </>
      )}
    </div>
  );
});

ErdEntityNode.displayName = 'ErdEntityNode';
